<template>
    <teleport to="body" :disabled="!appendToBody">
        <transition
            name="pt-drawer-fade"
            @after-enter="afterEnter"
            @after-leave="afterLeave"
            @before-leave="beforeLeave"
        >
            <pt-overlay
                v-show="visible"
                :mask="modal"
                :overlay-class="modalClass"
                :z-index="zIndex"
                @click="onModalClick"
            >
                <div
                    ref="drawerRef"
                    v-trap-focus
                    aria-modal="true"
                    aria-labelledby="pt-drawer__title"
                    :aria-label="title"
                    :class="['pt-drawer', direction, customClass]"
                    :style="isHorizontal ? 'width: ' + drawerSize : ''"
                    role="dialog"
                    @click.stop
                >
                    <header v-if="withHeader" id="pt-drawer__title" class="pt-drawer__header">
                        <div class="pt-drawer__header__title">
                            <slot name="title">
                                <div role="heading" :title="title">
                                    <pt-icon v-if="iconKey" :icon="iconKey" class="pt-drawer__title__icon"> </pt-icon>
                                    <span class="pt-drawer__title__content">
                                        {{ title }}
                                    </span>
                                </div>
                            </slot>
                        </div>

                        <button
                            v-if="showClose"
                            aria-label="close"
                            class="pt-drawer__close-btn"
                            type="button"
                            @click="handleClose"
                        >
                            <pt-icon icon="pt-icon-close" class="pt-dialog__close pt-icon pt-icon-close"></pt-icon>
                        </button>
                    </header>
                    <template v-if="rendered">
                        <section class="pt-drawer__body">
                            <template v-if="hasScroll">
                                <pt-scrollbar
                                    :style="{
                                        maxHeight: '90vh',
                                    }"
                                >
                                    <slot></slot>
                                </pt-scrollbar>
                            </template>
                            <template v-else>
                                <slot></slot>
                            </template>
                        </section>
                    </template>
                </div>
            </pt-overlay>
        </transition>
    </teleport>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue';
import { Overlay } from '../../overlay';
import useDialog from './useDialog';
import { TrapFocus } from '../../directives';

import type { PropType, SetupContext } from 'vue';

type Hide = (cancel: boolean) => void;
type DrawerDirection = 'ltr' | 'rtl' | 'ttb' | 'btt';

export default defineComponent({
    name: 'PtDrawer',
    components: {
        [Overlay.name]: Overlay,
    },
    directives: {
        TrapFocus,
    },
    props: {
        modelValue: {
            type: Boolean,
            required: true,
        },
        hasScroll: {
            type: Boolean,
            default: true,
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        beforeClose: Function as PropType<(hide: Hide) => void>,
        customClass: {
            type: String,
            default: '',
        },
        direction: {
            type: String as PropType<DrawerDirection>,
            default: 'btt',
            validator: (val: DrawerDirection) => {
                return ['ltr', 'rtl', 'ttb', 'btt'].indexOf(val) !== -1;
            },
        },
        showClose: {
            type: Boolean,
            default: true,
        },
        size: {
            type: [String, Number],
            default: '70%',
        },
        title: {
            type: String,
            default: '',
        },
        closeOnClickModal: {
            type: Boolean,
            default: true,
        },
        withHeader: {
            type: Boolean,
            default: true,
        },
        openDelay: {
            type: Number,
            default: 0,
        },
        closeDelay: {
            type: Number,
            default: 0,
        },
        zIndex: Number,
        modal: {
            type: Boolean,
            default: true,
        },
        modalFade: {
            type: Boolean,
            default: true,
        },
        modalClass: {
            type: String,
            default: 'noScroll',
        },
        lockScroll: {
            type: Boolean,
            default: true,
        },
        closeOnPressEscape: {
            type: Boolean,
            default: true,
        },
        destroyOnClose: {
            type: Boolean,
            default: false,
        },
        iconKey: {
            type: String,
            default: '',
        },
    },

    emits: ['open', 'opened', 'close', 'closed', 'update:modelValue'],

    setup(props, ctx) {
        const drawerRef = ref<HTMLElement>(null);
        return {
            ...useDialog(props, ctx as SetupContext, drawerRef),
            drawerRef,
            isHorizontal: computed(() => props.direction === 'rtl' || props.direction === 'ltr'),
            drawerSize: computed(() => (typeof props.size === 'number' ? `${props.size}px` : props.size)),
        };
    },
});
</script>
<style lang="scss" src="../style/drawer.scss" />
